import { resolveAlign, resolveExcelNumFmt } from '../../column-types';
import { buildGroupTree } from '../../grouping';
import type { DataGridColumn, DataGridGrouping, DataGridSummaryItem } from '../../types';
import { getExportHeader, resolveExportColumns } from '../value';
import { columnLetter } from './column-letter';
import { writeDataRow } from './data-row';
import { writeGroupNode } from './group-row';
import {
  applyBandedRows,
  autoSizeColumns,
  styleHeaderRow,
} from './header-and-decoration';
import type { SheetImage } from './images';
import { writeSheetImages } from './images';
import { writeSubtotalRow } from './subtotal-row';
import type { ExcelColumnFormat, ExcelWriteContext } from './types';

export interface BuildExcelOptions<T> {
  columns: DataGridColumn<T>[];
  rows: T[];
  sheetName?: string;
  currency?: string;
  grouping?: DataGridGrouping<T>;
  summary?: DataGridSummaryItem<T>[];
  showGrandTotal?: boolean;
}

/** Config for a single worksheet, consumed by `writeOneSheet`. */
export interface WriteSheetConfig<T> {
  /** Worksheet name (truncated to Excel's 31-char limit automatically). */
  sheetName: string;
  /** Data rows for this sheet. */
  data: T[];
  columns: DataGridColumn<T>[];
  /** Default currency code for `dataType: 'currency'` columns. Defaults to `'EUR'`. */
  currency?: string;
  grouping?: DataGridGrouping<T>;
  summary?: DataGridSummaryItem<T>[];
  /** Whether to append a grand-total row when `summary` is set. Defaults to `true`. */
  showGrandTotal?: boolean;
  /** Images to embed into this sheet (ex: signature PNGs). */
  images?: SheetImage[];
}

/**
 * Write a single fully-styled worksheet into an already-created workbook —
 * bold header (optionally with rotated/vertical headers), banded rows,
 * per-column number formats, group headers with merged cells, live
 * SUBTOTAL formulas, embedded images, freeze pane, auto filter and
 * auto-sized columns.
 *
 * This is the shared body used by both `buildExcel` (mono-sheet, kept for
 * backward compatibility) and `buildExcelMultiSheet` (N sheets in one
 * workbook, ex: US-C's weekly recap LUN..DIM + SEMAINE export).
 */
export async function writeOneSheet<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workbook: any,
  config: WriteSheetConfig<T>
): Promise<void> {
  const {
    sheetName,
    data: rows,
    columns,
    currency = 'EUR',
    grouping,
    summary,
    showGrandTotal = true,
    images,
  } = config;

  const sheet = workbook.addWorksheet(sheetName.slice(0, 31), {
    views: [{ state: 'frozen', ySplit: 1 }],
    // `summaryBelow: false` tells Excel that group summaries (headers +
    // subtotals) sit ABOVE the detail rows, which is exactly how we lay
    // things out. With this, the collapse `+`/`−` button is drawn on
    // the group-header line and stays visible when the group is folded.
    properties: {
      outlineProperties: {
        summaryBelow: false,
        summaryRight: false,
      },
    },
  });

  const exportCols = resolveExportColumns(columns);
  const hasSummary = !!summary && summary.length > 0;

  // Declare the worksheet columns. Note : we deliberately DO NOT set
  // `style` here — ExcelJS does not reliably propagate column-level
  // styles to cells added via `row.addRow({...})`. Instead every writer
  // applies `applyCellFormat` cell-by-cell, reading from the pre-built
  // `formatByColId` map below.
  sheet.columns = exportCols.map((col) => ({
    header: getExportHeader(col),
    key: col.id,
    // Slot columns (matrices de créneaux ex US-C, 40 colonnes 30 min) :
    // fines par défaut pour tenir l'ensemble à l'écran quand la feuille
    // est imprimée / consultée. autoSizeColumns skip aussi ce dataType.
    width: col.dataType === 'slot' ? 3 : 18,
  }));

  styleHeaderRow(sheet, exportCols);

  // Pre-compute two lookup maps :
  //   - letterByColId   : columnId → Excel letter (A, B, … AA, …) for formulas
  //   - formatByColId   : columnId → { numFmt, align } applied on each cell
  const letterByColId = new Map<string, string>();
  const formatByColId = new Map<string, ExcelColumnFormat>();
  exportCols.forEach((col, i) => {
    letterByColId.set(col.id, columnLetter(i + 1));
    formatByColId.set(col.id, {
      numFmt: resolveExcelNumFmt(col, currency),
      align: resolveAlign(col),
    });
  });

  const ctx: ExcelWriteContext<T> = {
    columns: exportCols,
    letterByColId,
    formatByColId,
    summary,
    currency,
  };

  // ---------- Body ----------
  let firstDataRow = -1;
  let lastDataRow = -1;

  if (grouping && rows.length > 0) {
    const tree = buildGroupTree(rows, grouping);
    for (const node of tree) {
      const range = writeGroupNode(sheet, node, ctx, hasSummary);
      if (range) {
        if (firstDataRow === -1) firstDataRow = range.firstDataRow;
        lastDataRow = range.lastDataRow;
      }
    }
  } else {
    for (const row of rows) {
      const rowNumber = writeDataRow(sheet, row, ctx);
      if (firstDataRow === -1) firstDataRow = rowNumber;
      lastDataRow = rowNumber;
    }
  }

  // ---------- Grand total ----------
  if (hasSummary && showGrandTotal && firstDataRow !== -1) {
    writeSubtotalRow(sheet, firstDataRow, lastDataRow, rows, 'Total', ctx, true);
  }

  // ---------- Images ----------
  if (images && images.length > 0) {
    writeSheetImages(workbook, sheet, images);
  }

  // ---------- Visual polish ----------
  applyBandedRows(sheet);
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: exportCols.length },
  };
  autoSizeColumns(sheet, exportCols);
}

/**
 * Build a fully-styled xlsx workbook and return it as a `Blob`. The output
 * looks like a proper report — bold header, banded rows, frozen pane,
 * auto filter, per-column number formats, group headers with merged
 * cells, and live SUBTOTAL formulas for subtotals / grand total.
 *
 * `ExcelJS` is pulled in via a dynamic import so the library only hits
 * the bundle when the user clicks "Exporter en Excel".
 *
 * Thin wrapper around `writeOneSheet` — kept as its own function (rather
 * than folding callers into `buildExcelMultiSheet` with a single-sheet
 * array) so the signature and behavior stay 100% backward compatible for
 * every existing caller.
 */
export async function buildExcel<T>(options: BuildExcelOptions<T>): Promise<Blob> {
  const {
    columns,
    rows,
    sheetName = 'Export',
    currency = 'EUR',
    grouping,
    summary,
    showGrandTotal = true,
  } = options;

  const { default: ExcelJS } = await import('exceljs');

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Bill App';
  workbook.created = new Date();
  // Force Excel to re-evaluate every formula on open. Belt-and-suspenders
  // alongside cached `result` values: if a third-party tool ever rewrites
  // the file with stale caches, Excel still computes from the formulas.
  workbook.calcProperties.fullCalcOnLoad = true;
  // 1904 date system (Mac Excel origine) — supporte nativement les durees
  // negatives (`[h]:mm` refuse d'afficher les valeurs < 0 en systeme 1900
  // par defaut, d'ou les cellules `#######`). ExcelJS convertit toutes les
  // `Date` JS selon ce flag → aucun risque de decalage +4 ans, meme pour
  // les colonnes date/datetime.
  workbook.properties.date1904 = true;

  await writeOneSheet(workbook, {
    sheetName,
    data: rows,
    columns,
    currency,
    grouping,
    summary,
    showGrandTotal,
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
